// src/contexts/ThemeContext.tsx
import { createContext } from 'preact'
import { useContext, useState, useEffect, useCallback, useMemo } from 'preact/hooks'
import { Theme, ThemeName } from '../types/theme'
import { themes } from '../themes/themes'

interface ThemeContextType {
  theme: Theme
  themeName: ThemeName
  setTheme: (themeName: ThemeName) => void
  availableThemes: ThemeName[]
}

const ThemeContext = createContext<ThemeContextType | null>(null)
const availableThemes: ThemeName[] = ['light', 'dark']

function applyThemeToDocument(theme: Theme) {
  const root = document.documentElement
  root.style.setProperty('--color-primary', theme.colors.primary)
  root.style.setProperty('--color-secondary', theme.colors.secondary)
  root.style.setProperty('--color-tertiary', theme.colors.tertiary)
  root.style.setProperty('--color-quaternary', theme.colors.quaternary)
  root.style.setProperty('--color-background', theme.colors.background)
  root.style.setProperty('--color-surface', theme.colors.surface)
  root.style.setProperty('--color-shell', theme.colors.shell)
  root.style.setProperty('--color-gradient-from', theme.colors.gradientFrom)
  root.style.setProperty('--color-gradient-to', theme.colors.gradientTo)
  root.style.setProperty('--color-text', theme.colors.text)
  root.style.setProperty('--color-text-secondary', theme.colors.textSecondary)
  root.style.setProperty('--color-border', theme.colors.border)
  root.style.setProperty('--color-success', theme.colors.success)
  root.style.setProperty('--color-warning', theme.colors.warning)
  root.style.setProperty('--color-error', theme.colors.error)
  root.style.setProperty('--color-info', theme.colors.info)

  root.style.setProperty('--spacing-xs', theme.spacing.xs)
  root.style.setProperty('--spacing-sm', theme.spacing.sm)
  root.style.setProperty('--spacing-md', theme.spacing.md)
  root.style.setProperty('--spacing-lg', theme.spacing.lg)
  root.style.setProperty('--spacing-xl', theme.spacing.xl)

  root.style.setProperty('--font-family', theme.typography.fontFamily)
  root.style.setProperty('--font-size-xs', theme.typography.fontSize.xs)
  root.style.setProperty('--font-size-sm', theme.typography.fontSize.sm)
  root.style.setProperty('--font-size-md', theme.typography.fontSize.md)
  root.style.setProperty('--font-size-lg', theme.typography.fontSize.lg)
  root.style.setProperty('--font-size-xl', theme.typography.fontSize.xl)

  root.style.setProperty('--chart-title-font-family', theme.typography.fontFamily)
  root.style.setProperty('--chart-title-font-size', '0.75rem')
  root.style.setProperty('--chart-title-font-weight', '600')
  root.style.setProperty('--chart-title-color', theme.colors.textSecondary)
  root.style.setProperty('--chart-title-letter-spacing', '0.025em')

  root.style.setProperty('--border-radius-sm', theme.borderRadius.sm)
  root.style.setProperty('--border-radius-md', theme.borderRadius.md)
  root.style.setProperty('--border-radius-lg', theme.borderRadius.lg)
  root.style.setProperty('--radius-sm', theme.borderRadius.sm)
  root.style.setProperty('--radius-md', theme.borderRadius.md)
  root.style.setProperty('--radius-lg', theme.borderRadius.lg)

  root.style.setProperty('--shadow-sm', theme.shadows.sm)
  root.style.setProperty('--shadow-md', theme.shadows.md)
  root.style.setProperty('--shadow-lg', theme.shadows.lg)
  root.style.setProperty('--shadow-card', theme.shadows.sm)
  root.style.setProperty(
    '--shadow-dropdown',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
  )
}

export function ThemeProvider({ children }: { children: preact.ComponentChildren }) {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeName | null
    return savedTheme === 'dark' || savedTheme === 'light' ? savedTheme : 'light'
  })
  const theme = themes[themeName]

  const setTheme = useCallback((newThemeName: ThemeName) => {
    setThemeName(newThemeName)
    localStorage.setItem('theme', newThemeName)
    applyThemeToDocument(themes[newThemeName])
  }, [])

  useEffect(() => {
    applyThemeToDocument(themes[themeName])
  }, [themeName])

  const contextValue = useMemo(
    () => ({
      theme,
      themeName,
      setTheme,
      availableThemes,
    }),
    [theme, themeName, setTheme],
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
